import { useEffect, useMemo, useState } from 'react';
import { HOTEL_SEARCH_DEBOUNCE_DELAY_MS } from '../constants';
import { REGIONS } from '../japaneseRegions';
import HotelSearchSelectOption from '../../../types/HotelSearchSelectOption';
import useSearchAutocomplete from './useSearchAutocomplete';
import englishCommon from '../../../public/locales/en/common.json';
import japaneseCommon from '../../../public/locales/ja/common.json';

const useHotelSearchOptions = () => {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchInputValue);
  const { data } = useSearchAutocomplete({
    term: debouncedSearchTerm,
  });

  // Debounce the search input value
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchInputValue);
    }, HOTEL_SEARCH_DEBOUNCE_DELAY_MS);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInputValue]);

  const defaultOptions = useMemo(() => {
    return REGIONS.flatMap((region) =>
      region.prefectures.map((prefecture) => {
        const key = (prefecture.name.split(':')[1] || '') as keyof typeof englishCommon;
        return {
          value: prefecture.path,
          label: {
            en: englishCommon[key],
            ja: japaneseCommon[key],
          },
          type: 'prefecture',
        };
      })
    ) as HotelSearchSelectOption[];
  }, []);

  const options = useMemo(() => {
    if (debouncedSearchTerm === '') {
      return defaultOptions;
    }

    return data?.data.map((item) => {
      const { type } = item;

      if (type === 'property') {
        return {
          value: item.propertyId,
          label: item.name,
          type,
          prefecture: item.prefecture,
          prefectureGroup: item.prefectureGroup,
        };
      }

      if (type === 'city') {
        return {
          type,
          value: `${item.location.lat},${item.location.lon}`,
          label: item.cityName,
          prefecture: item.prefectureName,
          location: item.location,
        };
      }

      return {
        value: item.id,
        label: item.name,
        type,
      };
    });
  }, [data, defaultOptions, debouncedSearchTerm]);

  return {
    searchInputValue,
    setSearchInputValue,
    defaultOptions,
    options,
  };
};

export default useHotelSearchOptions;
